if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        if (!this.form || !this.variantIdInput || !this.querySelector('[type="submit"]')) return;
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");
        this.upsellVariantId = this.dataset.upsellVariantId;

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitting || this.submitButton.disabled || this.submitButton.getAttribute("aria-disabled") === "true") return;
        this.handleErrorMessage();
        this.error = false;
        if (!this.variantIdInput?.value || this.variantIdInput.disabled) {
          this.handleErrorMessage(window.variantStrings.unavailable);
          return;
        }
        const quantity = Array.from(this.form.elements).find(input => input.name === "quantity");
        if (quantity && !quantity.checkValidity()) {
          quantity.reportValidity();
          return;
        }
        this.submitting = true;
        this.submitButton.setAttribute("aria-disabled", "true");
        this.submitButton.setAttribute("aria-busy", "true");
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner")?.classList.remove("hidden");
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];
        const formData = new FormData(this.form);
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        if (this.cart) {
          formData.set("sections", this.cart.getSectionsToRender().map(section => section.id));
          formData.set("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        const skipCart = this.variantIdInput.dataset.skipCart === "true";
        let added = false;
        let addAttempted = false;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);
        config.signal = controller.signal;
        try {
          // Optional gift must never make a normal add depend on a cart GET.
          if (this.upsellVariantId) {
            try {
              const stateResponse = await fetch(`${routes.cart_url}.js`, { signal: controller.signal });
              if (stateResponse.ok) {
                const state = await stateResponse.json();
                if (state.item_count === 0) {
                  await fetch(routes.cart_add_url, { ...this.getUpsellProductConfig(), signal: controller.signal });
                }
              }
            } catch (error) {
              if (controller.signal.aborted) throw error;
              console.warn("Optional cart gift could not be checked", error);
            }
          }
          addAttempted = true;
          const result = await fetch(routes.cart_add_url, config);
          const response = await result.json();
          if (!result.ok || response.status) {
            const message = response.description || response.message || window.cartStrings.error;
            const error = new Error(typeof message === "string" ? message : window.cartStrings.error);
            error.cartRejected = true;
            throw error;
          }
          added = true;
          document.dispatchEvent(new CustomEvent('nurturade:cart-add', {
            detail: { variantId: formData.get('id'), quantity: Number(formData.get('quantity') || 1) }
          }));
          if (skipCart) {
            window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
            return;
          }
          if (!this.cart) {
            window.location.assign(window.routes.cart_url);
            return;
          }
          const quickAddModal = this.closest("quick-add-modal");
          if (quickAddModal) quickAddModal.hide(true);
          this.cart.classList.remove("is-empty");
          this.cart.renderContents(response);
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form", productVariantId: formData.get("id"), cartData: response
          });
        } catch (error) {
          this.error = true;
          if (added) {
            // The POST succeeded: never offer another add because the drawer failed.
            window.location.assign(window.routes.cart_url);
            return;
          }
          let message = error.cartRejected ? error.message : window.cartStrings.error;
          if (addAttempted && !error.cartRejected) {
            message += document.documentElement.lang.startsWith('de')
              ? ' Bitte prüfe deinen Warenkorb, bevor du das Produkt erneut hinzufügst.'
              : ' Please check your cart before adding this product again.';
          }
          this.handleErrorMessage(message);
          this.errorMessageWrapper?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
          publish(PUB_SUB_EVENTS.cartError, {
            source: "product-form", productVariantId: formData.get("id"), errors: message, message
          });
          document.dispatchEvent(new CustomEvent('nurturade:cart-add-error', {
            detail: { variantId: formData.get('id'), message }
          }));
        } finally {
          clearTimeout(timeout);
          this.submitting = false;
          this.submitButton.classList.remove("loading");
          this.submitButton.removeAttribute("aria-disabled");
          this.submitButton.removeAttribute("aria-busy");
          this.querySelector(".loading__spinner")?.classList.add("hidden");
        }
      }

      getUpsellProductConfig() {
        const upsellFormData = new FormData();
        upsellFormData.append("id", this.upsellVariantId);
        upsellFormData.append("quantity", 1);

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];
        config.body = upsellFormData;

        return config;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper && errorMessage) {
          this.errorMessageWrapper = document.createElement('div');
          this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
          this.errorMessageWrapper.setAttribute('role', 'alert');
          this.errorMessageWrapper.innerHTML = '<span class="product-form__error-message"></span>';
          this.prepend(this.errorMessageWrapper);
        }
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
